import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "24px", alignItems: "center" },
  searchInput: { flex: 1, maxWidth: "360px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  letterhead: { textAlign: "center", borderBottom: "2px solid #1a237e", paddingBottom: "12px", marginBottom: "16px" },
  summaryBox: { background: "#f5f5f5", borderRadius: "8px", padding: "16px", marginTop: "16px" }
};

const GRADE_TONE = { A: "success", B: "success", C: "info", D: "warning", E: "danger", F: "danger" };

const round1 = n => Math.round(n * 10) / 10;

// Each row is a { course, result, status } entry as returned by GET /results/transcript's
// semesters[].courses[] — `result` is the ExamResult (or null if not yet graded), `status`
// is the Enrollment's status (enrolled/dropped), not the result's provisional/final status.
const transcriptColumns = [
  { id: "code", header: "Course Code", accessorFn: c => c.course?.code || "—", meta: { variant: "mono" } },
  { id: "name", header: "Course Name", accessorFn: c => c.course?.name || "—" },
  { id: "credits", header: "Credits", accessorFn: c => c.course?.creditUnits || "—" },
  {
    id: "cw",
    header: "CW (/60)",
    accessorFn: c => {
      const r = c.result;
      if (!r) return "—";
      return round1((r.ca1Score || 0) * 0.3 + (r.ca2Score || 0) * 0.2 + (r.practicalScore || 0) * 0.1);
    }
  },
  { id: "exam", header: "Exam (/40)", accessorFn: c => (c.result ? round1((c.result.examScore || 0) * 0.4) : "—") },
  { id: "total", header: "Total", accessorFn: c => (c.result?.totalScore != null ? `${c.result.totalScore}%` : "—") },
  {
    id: "grade",
    header: "Grade",
    cell: info => {
      const grade = info.row.original.result?.grade || "—";
      return grade !== "—" ? <Pill tone={GRADE_TONE[grade] || "muted"}>{grade}</Pill> : "—";
    }
  },
  {
    id: "status",
    header: "Result Status",
    cell: info => {
      const status = info.row.original.result?.status || "pending";
      return <Pill tone={status === "final" ? "success" : "warning"}>{status}</Pill>;
    }
  }
];

class Transcript extends Component {
  state = {
    studentNo: "",
    student: null,
    semesters: [],
    cgpa: "0.00",
    loading: false,
    error: null
  };

  handleSearch = async () => {
    const { studentNo } = this.state;
    if (!studentNo.trim()) return;
    this.setState({ loading: true, error: null, student: null, semesters: [], cgpa: "0.00" });
    try {
      const res = await api.get("/results/transcript", { params: { studentNo: studentNo.trim() } });
      const d = res.data.data || res.data;
      this.setState({ student: d.student || null, semesters: d.semesters || [], cgpa: d.cgpa || "0.00", loading: false });
    } catch (e) {
      this.setState({ error: "Student not found or no results available.", loading: false });
    }
  };

  // Server-computed `cgpa` (state) includes all graded courses regardless of
  // provisional/final status. This mirrors that same weighting but restricted
  // to courses whose result has been finalised, for the "Final Only" figure.
  calcFinalGPA = (courses) => {
    const finalCourses = courses.filter(c => c.result && c.result.status === "final");
    if (!finalCourses.length) return "0.00";
    let totalPoints = 0, totalCredits = 0;
    finalCourses.forEach(c => {
      const credits = c.course?.creditUnits || 3;
      totalPoints += (c.result.gradePoint || 0) * credits;
      totalCredits += credits;
    });
    return totalCredits > 0 ? (totalPoints / totalCredits).toFixed(2) : "0.00";
  };

  handlePrint = () => { window.print(); };

  render() {
    const { classes } = this.props;
    const { studentNo, student, semesters, cgpa, loading, error } = this.state;

    const allCourses = semesters.flatMap(s => s.courses);
    const gpa = cgpa;
    const finalCgpa = this.calcFinalGPA(allCourses);

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Student Transcript</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Academic results and grade history per semester
                  </p>
                </div>
            </CardHeader>
            <CardBody>
              <div className={classes.searchBar}>
                <input
                  className={classes.searchInput}
                  type="text"
                  placeholder="Enter student number (e.g. PTC/2024/001)"
                  value={studentNo}
                  onChange={e => this.setState({ studentNo: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && this.handleSearch()}
                />
                <Button onClick={this.handleSearch} color="primary" disabled={loading}>
                  {loading ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Generate Transcript"}
                </Button>
              </div>

              {error && <p className={classes.errorMsg}>{error}</p>}

              {student && (
                <div>
                  {/* Letterhead */}
                  <div className={classes.letterhead}>
                    <h3 style={{ margin: 0, color: "#1a237e", fontWeight: "bold" }}>BISHOP WILLIS CORE PTC</h3>
                    <p style={{ margin: "2px 0", color: "#666", fontSize: "13px" }}>Iganga, Uganda | P.O. Box XX</p>
                    <h4 style={{ margin: "8px 0 0", color: "#333" }}>OFFICIAL ACADEMIC TRANSCRIPT</h4>
                  </div>

                  {/* Student Details */}
                  <GridContainer style={{ marginBottom: "16px" }}>
                    <GridItem xs={12} sm={6}>
                      <table style={{ width: "100%", fontSize: "13px" }}>
                        <tbody>
                          {[
                            ["Student Name", `${student.firstName || ""} ${student.lastName || ""}`.trim()],
                            ["Student No.", student.studentNo],
                            ["Programme", student.programme?.name || "—"],
                            ["Year of Study", student.yearOfStudy || "—"]
                          ].map(([l, v]) => (
                            <tr key={l}>
                              <td style={{ fontWeight: "bold", color: "#555", padding: "3px 12px 3px 0", width: "130px" }}>{l}:</td>
                              <td style={{ color: "#333" }}>{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </GridItem>
                    <GridItem xs={12} sm={6}>
                      <table style={{ width: "100%", fontSize: "13px" }}>
                        <tbody>
                          {[
                            ["Enrollment Date", student.enrollmentDate || "—"],
                            ["Status", student.status || "—"],
                            ["Date of Issue", new Date().toLocaleDateString("en-UG")]
                          ].map(([l, v]) => (
                            <tr key={l}>
                              <td style={{ fontWeight: "bold", color: "#555", padding: "3px 12px 3px 0", width: "130px" }}>{l}:</td>
                              <td style={{ color: "#333" }}>{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </GridItem>
                  </GridContainer>

                  {/* Results per Semester */}
                  {semesters.map(s => (
                    <div key={s.semester?.id || Math.random()} style={{ marginBottom: "20px" }}>
                      <p style={{ fontWeight: "bold", color: "#1a237e", borderBottom: "1px solid #e0e0e0", paddingBottom: "4px", marginBottom: "8px" }}>
                        {s.semester?.name || "Unknown Semester"}
                      </p>
                      <DataTable
                        columns={transcriptColumns}
                        data={s.courses}
                        emptyState="No results for this semester."
                      />
                    </div>
                  ))}

                  {/* Summary */}
                  <div className={classes.summaryBox}>
                    <GridContainer>
                      {[
                        { label: "Total Courses", value: allCourses.length },
                        { label: "GPA (All)", value: gpa },
                        { label: "CGPA (Final Only)", value: finalCgpa },
                        { label: "Courses Passed", value: allCourses.filter(c => c.result?.grade && !["E", "F"].includes(c.result.grade)).length }
                      ].map(s => (
                        <GridItem xs={6} sm={3} key={s.label} style={{ textAlign: "center" }}>
                          <p style={{ fontWeight: "bold", fontSize: "22px", color: "#1a237e", margin: 0 }}>{s.value}</p>
                          <p style={{ fontSize: "12px", color: "#666", margin: 0 }}>{s.label}</p>
                        </GridItem>
                      ))}
                    </GridContainer>
                  </div>

                  <p style={{ fontSize: "11px", color: "#999", marginTop: "16px", textStyle: "italic" }}>
                    This is an official transcript generated from the Bishop Willis Core PTC Management System.
                    Grade Scale: A=80-100 (5.0), B=70-79 (4.0), C=60-69 (3.0), D=50-59 (2.0), E=40-49 (1.0), F=0-39 (0.0)
                  </p>
                </div>
              )}
            </CardBody>
            {student && (
              <CardFooter style={{ justifyContent: "flex-end", padding: "8px 20px 20px" }}>
                <Button onClick={this.handlePrint} color="primary">
                  Print / Save PDF
                </Button>
              </CardFooter>
            )}
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

Transcript.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(Transcript));
